import { envelopeData } from '@/lib/envelope'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { categoriesQueryOptions, searchCoursesQueryOptions } from '@/lib/queries/catalog'
import { CatalogHeader } from '@/components/catalog/catalog-header'
import { CatalogFilterBar } from '@/components/catalog/catalog-filter-bar'
import { CourseCarousel } from '@/components/catalog/course-carousel'
import { CourseCard } from '@/components/catalog/course-card'
import { CatalogSkeleton } from '@/components/shared/loading-skeleton'

export const Route = createFileRoute('/_app/catalogo')({
  loader: async ({ context }) => {
    try {
      return await context.queryClient.fetchQuery(categoriesQueryOptions())
    } catch {
      return null
    }
  },
  component: CatalogPage,
  pendingComponent: () => <CatalogSkeleton />,
})

function CatalogPage() {
  const initialData = Route.useLoaderData()
  const [search, setSearch] = useState('')

  // o loader devolve `null` quando a busca falha — e `initialData: null` faz o
  // react-query tratar a falha como resposta válida (lista vazia sem erro).
  const { data: categoriesData } = useQuery({ ...categoriesQueryOptions(), initialData: initialData ?? undefined })
  const categories = envelopeData(categoriesData) ?? []

  const { data: searchData } = useQuery({
    ...searchCoursesQueryOptions({ q: search }),
    enabled: search.length >= 2,
  })
  const searchResults = envelopeData(searchData) ?? []

  const isSearching = search.length >= 2

  return (
    <div className="space-y-8 max-w-[1600px] mx-auto">
      <div className="space-y-4">
        <CatalogHeader />
        <CatalogFilterBar search={search} onSearchChange={setSearch} />
      </div>

      {isSearching ? (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">
            Resultados para &ldquo;{search}&rdquo;
          </h2>
          {searchResults.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground">Nenhum resultado encontrado.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {searchResults.map((course: any) => (
                <CourseCard
                  key={course.courseId}
                  course={{
                    id: course.courseId,
                    title: course.courseName,
                    href: `/${course.menuSlug}/${course.courseSlug}`,
                    thumbnailUrl: course.thumbnailUrl,
                    description: course.description,
                    totalLessons: course.totalLessons,
                    instructorName: course.instructorName,
                    hasAccess: course.hasAccess,
                    price: course.price,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-10">
          {categories.map((category: any) => (
            <CourseCarousel
              key={category.categoryId}
              title={category.categoryName}
              courses={(category.courses ?? []).map((c: any) => ({
                id: c.courseId,
                title: c.courseName,
                href: `/${c.menuSlug}/${c.courseSlug}`,
                thumbnailUrl: c.thumbnailUrl,
                description: c.description,
                totalLessons: c.totalLessons,
                instructorName: c.instructorName,
                hasAccess: c.hasAccess,
                price: c.price,
              }))}
            />
          ))}

          {categories.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground">
                Nenhum conteúdo disponível no momento.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
